import React, { FunctionComponent } from "react";
import { CircularProgress, Container, Grid, Stack } from "@mui/material";
import { getSafeReturnUrl } from "merchant-core";
import { Banner } from "../components/Banner";
import { Checklist } from "../components/Checklist";
import { ChecklistItem } from "../components/ChecklistItem";
import { useOnboardingMerchant } from "../hooks/api/useOnboardingMerchant";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { GetStaticProps } from "next";
import { CompleteOnboarding } from "../components/CompleteOnboarding";
import { useRouteGuards } from "../hooks/useRouteGuards";
import { isMerchantDeclined } from "../utils/decline";
import { NextRouter, useRouter } from "next/router";
import { Footer } from "../components/Footer";
import CalendarIcon from "../components/Icons/CalendarIcon";
import { wix } from "../models/platform";

const getReturnUrl = (router: NextRouter): string | null => {
  return getSafeReturnUrl(router?.query?.returnUrl, "/");
};

const getCurrentPath = (): string | null => {
  if (!window?.location) {
    return null;
  }

  return window.location.pathname + window.location.search;
};

const shouldRedirect = (router: NextRouter): boolean => {
  const returnUrl = getReturnUrl(router);
  if (!returnUrl) {
    return false;
  }

  const currentPath = getCurrentPath();
  if (!currentPath) {
    return false;
  }

  return returnUrl !== currentPath;
};

const Index: FunctionComponent = () => {
  const { t } = useTranslation("home");
  const { data: onboardingMerchant, isLoading } = useOnboardingMerchant();
  const router = useRouter();
  const routeGuards = useRouteGuards();

  const redirectRequired = shouldRedirect(router);
  const redirectUrl = getReturnUrl(router);
  if (redirectRequired && !!redirectUrl) {
    router.push(redirectUrl);
    return <></>;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" mt="40px">
        <CircularProgress size={50} color="secondary" />
      </Stack>
    );
  }

  if (!onboardingMerchant) {
    return <>No onboarding merchant!</>;
  }

  if (isMerchantDeclined(onboardingMerchant)) {
    router.push("/sorry");
    return <></>;
  }

  const businessName = onboardingMerchant.businessInfo?.businessName;
  const platform =
    onboardingMerchant.businessInfo?.platform ?? onboardingMerchant.platform;

  const isInReview = onboardingMerchant.isInReview;
  const shouldSkipCompletionRequest = platform === wix.systemName;

  const isMsaReady = !!onboardingMerchant.dueDiligenceSucceeded;

  // Onboarding is completed sooner if integration verification is not required
  let isOnboardingComplete = false;
  if (onboardingMerchant.isIntegrationVerificationRequiredForOnboarding) {
    isOnboardingComplete = !!onboardingMerchant.integrationVerified;
  } else {
    isOnboardingComplete =
      !!onboardingMerchant.dueDiligenceSucceeded &&
      !!onboardingMerchant.msaSigned;
  }

  const banner = isInReview ? (
    <Banner title={t("in-review.title")} subtitle={t("in-review.subtitle")} />
  ) : (
    <Banner title={t("banner.title")} subtitle={t("banner.subtitle")} />
  );

  return (
    <Container maxWidth="lg" id="containerDashboard">
      <Grid container>
        <Grid item xs={12} sx={{ marginTop: "20px" }}>
          {banner}

          <Checklist>
            <ChecklistItem
              data-testid="accountDetails"
              estimatedTime={t("checklist.accountDetails.estimatedTime")}
              primaryText={t("checklist.accountDetails.stepName")}
              checked={onboardingMerchant.businessInfoCompleted}
              href="/business-info"
              disabled={!routeGuards.businessInfo(onboardingMerchant)}
            />

            <ChecklistItem
              data-testid="businessInfo"
              estimatedTime={t("checklist.businessInfo.estimatedTime")}
              primaryText={t("checklist.businessInfo.stepName", {
                businessName: businessName || "your business",
              })}
              disabled={!routeGuards.complianceInfo(onboardingMerchant)}
              checked={onboardingMerchant.complianceInfoCompleted}
              href="/tell-us-more"
            />

            <ChecklistItem
              data-testid="linkBankAccount"
              estimatedTime={t("checklist.linkBankAccount.estimatedTime")}
              primaryText={t("checklist.linkBankAccount.stepName")}
              disabled={!routeGuards.stripeConnect(onboardingMerchant)}
              hidden={onboardingMerchant.stripeIntegrationBypassed}
              checked={onboardingMerchant.stripeAccountCompleted}
              href="/bank-account"
            />

            <ChecklistItem
              data-testid="inReview"
              estimatedTime={t("checklist.inReview.estimatedTime")}
              primaryText={t("checklist.inReview.stepName")}
              icon={<CalendarIcon />}
              disabled={!onboardingMerchant.isInReview}
              checked={onboardingMerchant.dueDiligenceSucceeded}
              href="/review"
              tooltip={t("checklist.inReview.tooltip")}
            />

            <ChecklistItem
              data-testid="msaSigned"
              primaryText={t("checklist.msaSigned.stepName")}
              estimatedTime={t("checklist.msaSigned.estimatedTime")}
              checked={onboardingMerchant.msaSigned}
              disabled={onboardingMerchant.msaSigned || !isMsaReady}
              tooltip={
                isMsaReady ? t("checklist.msaSigned.tooltip") : undefined
              }
              href="/merchant-service-agreement"
            />

            {/* if integration verification is not required don't show these last steps*/}
            {!onboardingMerchant.isIntegrationVerificationRequiredForOnboarding ? (
              <></>
            ) : (
              <>
                <ChecklistItem
                  data-testid="addWidget"
                  estimatedTime={t("checklist.addWidget.estimatedTime")}
                  hidden={!platform || platform == "custom"}
                  primaryText={t("checklist.addWidget.stepName")}
                  disabled={!routeGuards.widgetIntegration(onboardingMerchant)}
                  checked={onboardingMerchant.widgetVerified}
                  href="/widget"
                />

                <ChecklistItem
                  data-testid="addCheckout"
                  estimatedTime={t("checklist.addCheckout.estimatedTime")}
                  primaryText={t("checklist.addCheckout.stepName")}
                  checked={onboardingMerchant.integrationVerified}
                  disabled={!routeGuards.pluginIntegration(onboardingMerchant)}
                  href="/checkout"
                />
              </>
            )}

            {isOnboardingComplete ? (
              <CompleteOnboarding
                skipCompletionRequest={shouldSkipCompletionRequest}
              />
            ) : (
              <></>
            )}
          </Checklist>
        </Grid>
      </Grid>

      <Footer />
    </Container>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale && (await serverSideTranslations(locale, ["common", "home"]))),
    },
  };
};
